import { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { FileText, Search, Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/App';
import { isDemoUser } from '@/lib/utils';
import AdminNavigation from '@/components/AdminNavigation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';

import { useConfirm } from '@/components/ConfirmDialog';

import { HistoryStatCards } from './HistoryStatCards';
import { DraftCard } from './DraftCard';
import { BulkRegistrationCard } from './BulkRegistrationCard';
import { API } from './helpers';

export default function AdminBulkHistory() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isDemo = isDemoUser(user);
  const confirmDialog = useConfirm();
  const [bulkRegistrations, setBulkRegistrations] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [filteredRegistrations, setFilteredRegistrations] = useState([]);
  const [areaLookup, setAreaLookup] = useState({});

  const fetchBulkHistory = useCallback(async () => {
    try {
      if (isDemo) {
        const demoRegistrations = JSON.parse(localStorage.getItem('demo_bulk_registrations') || '[]');
        const demoDrafts = JSON.parse(localStorage.getItem('bulk_entry_draft') ? '[' + localStorage.getItem('bulk_entry_draft') + ']' : '[]');
        
        setBulkRegistrations(demoRegistrations);
        if (demoDrafts.length > 0 && demoDrafts[0].sampleDate) {
          setDrafts([{
            id: 'demo-draft',
            sample_date: demoDrafts[0].sampleDate,
            selected_area: demoDrafts[0].selectedArea,
            updated_at: demoDrafts[0].timestamp,
            isDraft: true
          }]);
        }
        setLoading(false);
        return;
      }
      
      const [regResponse, draftResponse, areasResponse] = await Promise.all([
        axios.get(`${API}/bulk-registrations`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/bulk-drafts`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/areas`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      
      // Build area ID → name lookup
      const lookup = {};
      (areasResponse.data || []).forEach(a => { lookup[a.id] = a.name; });
      setAreaLookup(lookup);
      
      setBulkRegistrations(regResponse.data);
      setDrafts(draftResponse.data.map(d => ({ ...d, isDraft: true })));
      setLoading(false);
    } catch (error) {
      console.error('Error fetching bulk history:', error);
      setLoading(false);
    }
  }, [isDemo]);

  const filterRegistrations = useCallback(() => {
    const draftsWithSort = drafts.map(d => ({ ...d, _sortDate: d.sample_date || d.updated_at || '' }));
    const regsWithSort = bulkRegistrations.map(r => ({ ...r, _sortDate: r.date || r.created_at || '' }));
    
    let combined = [];
    if (statusFilter === 'all') combined = [...draftsWithSort, ...regsWithSort];
    else if (statusFilter === 'draft') combined = [...draftsWithSort, ...regsWithSort.filter(r => !r.sent_to_customer && r.status !== 'sent')];
    else if (statusFilter === 'sent') combined = regsWithSort.filter(r => r.sent_to_customer || r.status === 'sent');
    
    // Sort all items together: drafts first within same date, then by sample date descending
    combined.sort((a, b) => {
      // Drafts always float to top above registrations with same or older dates
      if (a.isDraft && !b.isDraft) return -1;
      if (!a.isDraft && b.isDraft) return 1;
      // Both drafts: sort by sample_date descending, then updated_at
      if (a.isDraft && b.isDraft) {
        const dateA = a.sample_date || '';
        const dateB = b.sample_date || '';
        if (dateA !== dateB) return dateB.localeCompare(dateA);
        return new Date(b.updated_at || 0) - new Date(a.updated_at || 0);
      }
      // Both registrations: sort by date descending
      return (b.date || '').localeCompare(a.date || '');
    });
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      combined = combined.filter(reg => {
        if (reg.isDraft) return reg.sample_date?.includes(term) || reg.selected_area?.toLowerCase().includes(term);
        return reg.title?.toLowerCase().includes(term) || reg.area_name?.toLowerCase().includes(term) || reg.date?.includes(term) || reg.biologist?.toLowerCase().includes(term);
      });
    }
    setFilteredRegistrations(combined);
  }, [drafts, bulkRegistrations, searchTerm, statusFilter]);

  useEffect(() => {
    fetchBulkHistory();
  }, [fetchBulkHistory]);

  useEffect(() => {
    filterRegistrations();
  }, [filterRegistrations]);

  const deleteBulkRegistration = async (id) => {
    const ok = await confirmDialog({
      title: 'Slette registrering?',
      message: 'Er du sikker på at du vil slette denne registreringa? Handlinga kan ikkje angrast.',
      confirmText: 'Ja, slett',
      variant: 'danger'
    });
    if (!ok) return;
    
    try {
      await axios.delete(`${API}/bulk-registrations/${id}`, { withCredentials: true });
      toast.success('Bulk registrering slettet');
      fetchBulkHistory();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Kunne ikke slette registrering');
    }
  };

  const deleteDraft = async (id) => {
    const ok = await confirmDialog({
      title: 'Slette utkast?',
      message: 'Er du sikker på at du vil slette dette utkastet?',
      confirmText: 'Ja, slett',
      variant: 'danger'
    });
    if (!ok) return;
    
    try {
      if (isDemo) {
        localStorage.removeItem('bulk_entry_draft');
      } else {
        await axios.delete(`${API}/bulk-drafts/${id}`, { withCredentials: true });
      }
      toast.success('Utkast slettet');
      fetchBulkHistory();
    } catch (error) {
      console.error('Delete draft error:', error);
      toast.error('Kunne ikke slette utkast');
    }
  };

  const openDraft = (draft) => {
    const draftId = typeof draft === 'object' ? draft.id : draft;
    if (draftId) {
      navigate(`/admin/samples/bulk?draft=${draftId}`);
    } else {
      navigate('/admin/samples/bulk');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-teal-50/30 flex">
        <AdminNavigation />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-10 h-10 animate-spin text-teal-600 mx-auto mb-3" />
            <p className="text-slate-600 text-sm">Laster bulk registreringer...</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-teal-50/30 flex">
      <AdminNavigation />
      <main className="flex-1 p-4 lg:p-8 overflow-x-hidden">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">Prøver</h1>
              <p className="text-sm text-slate-600 mt-1">Administrer og se registrerte prøver</p>
            </div>
            
            <Link to="/admin/samples/bulk?new=1">
              <Button className="bg-teal-600 hover:bg-teal-700" size="sm">
                <Plus className="w-4 h-4 mr-2" />
                Ny registrering
              </Button>
            </Link>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3 mb-5">
            {/* Status chips */}
            <div className="flex items-center gap-1.5">
              {[
                { id: 'all', label: `Alle (${drafts.length + bulkRegistrations.length})` },
                { id: 'draft', label: `Utkast (${drafts.length + bulkRegistrations.filter(r => !r.sent_to_customer && r.status !== 'sent').length})` },
                { id: 'sent', label: `Sendt (${bulkRegistrations.filter(r => r.sent_to_customer || r.status === 'sent').length})` },
              ].map(f => (
                <button key={f.id} onClick={() => setStatusFilter(f.id)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    statusFilter === f.id ? 'bg-teal-600 text-white shadow-sm' : 'bg-white text-slate-600 border border-slate-200 hover:border-slate-300'
                  }`}>{f.label}</button>
              ))}
            </div>
            
            {/* Search */}
            <div className="relative flex-1 min-w-[200px] max-w-[350px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input type="text" placeholder="Søk..." value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)} className="pl-9 h-9 text-sm" />
            </div>
          </div>

          {/* Bulk Registrations List */}
          {filteredRegistrations.length === 0 ? (
            <Card className="shadow-sm">
              <CardContent className="p-8 text-center">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-600 mb-2">Ingen prøver funnet</p>
                <p className="text-sm text-slate-500">Opprett din første prøveregistrering for å komme i gang</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredRegistrations.map((bulk) => {
                if (bulk.isDraft) {
                  return (
                    <DraftCard
                      key={bulk.id}
                      draft={bulk}
                      areaLookup={areaLookup}
                      onOpen={openDraft}
                      onDelete={deleteDraft}
                    />
                  );
                }
                
                return (
                  <BulkRegistrationCard
                    key={bulk.id}
                    bulk={bulk}
                    isDemo={isDemo}
                    onRefresh={fetchBulkHistory}
                    onDelete={deleteBulkRegistration}
                  />
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
